import * as React from 'react';

import { getSupportedCurrencies } from 'react-native-format-currency';

import { BasisOfAccountingPreference } from '../../Models/Api/strongbox.financialportal';
import { PortalCreateSubmissionRequestOptions, PortalSubmissionRequestDefaultFullFiscalYears } from '../../Models/PortalCreateSubmissionRequestOptions';

import { faCircleInfo } from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Input, Select } from '../design-system/components';

import { MostRecentDate } from '../../Utils/MostRecentDate';

import {
    ConnectorType,
    ImportMetadata,
    ImportMetadataKeys,
    LinkMode,
} from '../../Store/ImportFinancials';

import { PortalTextCommon, PortalTextImportModals } from '../../Models/Api/strongbox.financialportal';

export enum OnOffSettings {
    anonymizeCustomersAndVendors,
    userGetsCopy,
    allowUserUpload
};

// Format a Date as the yyyy-MM-dd string a native <input type="date"> expects (local calendar date, no
// timezone shift).
const toDateInputValue = (d: Date): string => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

type Props = {
    showShareableLinkParameters: boolean;
    directLink: boolean;
    disableFileUpload?: boolean;
    disableProvideUserCopy?: boolean;
    disableAnonymizedResults?: boolean;
    disableBasisOfAccounting?: boolean;
    allUserOptionsDisabled?: boolean;
    parameters: PortalCreateSubmissionRequestOptions;
    maxCollectionPeriods: number;
    onMostRecentMonthChange?: (newValue: MostRecentDate) => void;
    onPeriodsToCollectChanged?: (newValue: number) => void;
    onToggleOnOffSetting?: (setting: OnOffSettings) => void;
    onBasisOfAccountingChanged?: (newValue: BasisOfAccountingPreference) => void;
    onCollectThroughChanged?: (newValue: 'Today' | 'LastFullMonth') => void;
    numberOfMonthsInDropdown?: number;
    idPrefix?: string;
    noTimeRestrictionForDataCollection?: boolean;
    linkMode: LinkMode;
    replaceCalendarWithMonthEndOrToday?: boolean;
    importModalsText?: PortalTextImportModals;
    commonText?: PortalTextCommon;
    connectorType: ConnectorType;
    importMetadata: ImportMetadata[];
    updateMetadata: (key: string, value: string) => void;
    // Set when the user pressed Next on the universal-connector step with an empty company name: show the
    // field's red border + "required" message instead of blocking via a disabled Next button.
    companyNameError?: boolean;
}

export const CollectFinancialsImportParameters: React.FC<Props> = (props): React.ReactElement => {
    const {
        noTimeRestrictionForDataCollection,
        directLink,
        disableFileUpload,
        disableProvideUserCopy,
        disableBasisOfAccounting,
        disableAnonymizedResults,
        allUserOptionsDisabled,
        onToggleOnOffSetting,
        onBasisOfAccountingChanged,
        onMostRecentMonthChange,
        onPeriodsToCollectChanged,
        parameters,
        maxCollectionPeriods,
        numberOfMonthsInDropdown,
        idPrefix,
        showShareableLinkParameters,
        replaceCalendarWithMonthEndOrToday,
        onCollectThroughChanged,
        importModalsText,
        commonText,
        connectorType,
        importMetadata,
        updateMetadata,
        companyNameError,
    } = props;

    const {
        allowUserUpload,
        anonymizeCustomersAndVendors,
        basisOfAccountingPreference,
        fullFiscalYearsToCollect,
        mostRecentPeriodMonth,
        mostRecentPeriodYear,
        mostRecentPeriodDay,
        provideUserCopy,
    } = props.parameters;

    const [fullFYearsToCollect, setFullFYearsToCollect] = React.useState<number>(() => {
        return fullFiscalYearsToCollect !== 0 ? fullFiscalYearsToCollect : PortalSubmissionRequestDefaultFullFiscalYears;
    });
    const [currentYearOnly, setCurrentYearOnly] = React.useState<boolean>(false);
    const [showBasisTooltip, setShowBasisTooltip] = React.useState<boolean>(false);
    const [datePickerInputValue, setDatePickerInputValue] = React.useState<string>('');

    React.useEffect(() => {
        setCurrentYearOnly(fullFiscalYearsToCollect === 0)

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullFiscalYearsToCollect]);

    const currencies = React.useMemo(() => {
        const currencies = getSupportedCurrencies();
        return currencies;
    }, []);

    const ucParameters = React.useMemo(() => {
        let companyName = '';
        let currency = '';
        let fiscalYearEndMonth = '';

        importMetadata.forEach(m => {
            switch (m.key) {
                case ImportMetadataKeys.ucMetadataCompanyName:
                    companyName = m.value;
                    break;
                case ImportMetadataKeys.ucMetadataCurrency:
                    currency = m.value;
                    break;
                case ImportMetadataKeys.ucFiscalEndMonth:
                    fiscalYearEndMonth = m.value;
                    break;
            }
        });

        return {
            companyName,
            currency,
            fiscalYearEndMonth,
        };

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [importMetadata]);

    const datePickerParameters = React.useMemo(() => {
        if ((mostRecentPeriodMonth === undefined) ||
            (mostRecentPeriodYear === undefined) ||
            (mostRecentPeriodDay === undefined) ||
            (numberOfMonthsInDropdown === undefined)) {
            return undefined;
        }

        const today = new Date();
        const earliestDate = new Date(today.getFullYear(), today.getMonth() - numberOfMonthsInDropdown + 1, 1);

        let currentDay: Date;

        currentDay = new Date(
            mostRecentPeriodYear,
            mostRecentPeriodMonth,
            mostRecentPeriodDay
        );

        if (currentDay > today) {
            currentDay = today;
        } else if (currentDay < earliestDate) {
            currentDay = earliestDate;
        }

        const result = {
            minDate: earliestDate,
            maxDate: today,
            importDay: currentDay
        }

        return result;

        // These are the dependencies I actually want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mostRecentPeriodMonth, mostRecentPeriodYear, mostRecentPeriodDay, numberOfMonthsInDropdown]);

    React.useEffect(() => {
        // Pretty unlikely that datePickerParameters doesn't have a value, this is mostly a sanity check.
        if (!!datePickerParameters) {
            setDatePickerInputValue(toDateInputValue(datePickerParameters.importDay));
        }
    }, [datePickerParameters])

    const excelImportParameters = (): React.ReactElement => (
        <>
            <div className={'page-divider'} />
            <div className={'page-stack page-stack--tight'}>
                <span className={'page-section-label'}>{importModalsText?.pleaseNote || ''}</span>
                <span className={'page-help-text page-help-text--italic'}>{importModalsText?.uploadTemplateDatesNote || ''}</span>
            </div>
        </>
    );

    const basisOfAccounting = (): React.ReactElement => {
        if (disableBasisOfAccounting === true) {
            return (<React.Fragment />);
        }
        return (
            <>
                <label className={'page-radio-row'}>
                    <input
                        type={'radio'}
                        className={'cr-radio'}
                        name={`${idPrefix}-Basis`}
                        id={`${idPrefix}-id-Accrual`}
                        checked={!!parameters && (!basisOfAccountingPreference || basisOfAccountingPreference === 'Accrual')}
                        disabled={!parameters}
                        onChange={() => onBasisOfAccountingChanged && onBasisOfAccountingChanged('Accrual')}
                    />
                    <span className={'page-radio-row__label'}>{commonText?.basisOfAccountingAccrual || ''}</span>
                </label>
                <label className={'page-radio-row'}>
                    <input
                        type={'radio'}
                        className={'cr-radio'}
                        name={`${idPrefix}-Basis`}
                        id={`${idPrefix}-id-Cash`}
                        checked={!!parameters && basisOfAccountingPreference === 'Cash'}
                        disabled={!parameters}
                        onChange={() => onBasisOfAccountingChanged && onBasisOfAccountingChanged('Cash')}
                    />
                    <span className={'page-radio-row__label'}>{commonText?.basisOfAccountingCash || ''}</span>
                </label>
            </>
        );
    }

    const accountingPkgImportParameters = (): React.ReactElement => {
        // Show each option only when it applies (hide, don't disable). "Give a copy" / "allow upload" are
        // shareable-link only, so they are hidden for a direct connection. If nothing is left to show, drop
        // the whole section — including the divider above it.
        const shareableOptionsApply = showShareableLinkParameters && !!parameters && !directLink;
        const showAnonymize = (allUserOptionsDisabled !== true) && (disableAnonymizedResults !== true);
        const showGiveCopy = shareableOptionsApply && (disableProvideUserCopy !== true);
        const showAllowUpload = shareableOptionsApply && (disableFileUpload !== true);

        if (!showAnonymize && !showGiveCopy && !showAllowUpload) {
            return <React.Fragment />;
        }

        return (
            <>
                <div className={'page-divider'} />
                <div className={'page-stack'}>
                    <span className={'page-section-label page-section-label--negative-mb'}>{importModalsText?.importStepsOptions || ''}</span>
                    {showAnonymize && (
                        <label className={'page-checkbox-row'}>
                            <input
                                type={'checkbox'}
                                className={'cr-checkbox page-checkbox-row__input'}
                                name={`${idPrefix}-AnonCustomersAndVendors`}
                                id={`${idPrefix}-anon-customers-and-vendors`}
                                checked={!!parameters && anonymizeCustomersAndVendors}
                                disabled={!parameters}
                                onChange={() => onToggleOnOffSetting && onToggleOnOffSetting(OnOffSettings.anonymizeCustomersAndVendors)}
                            />
                            <div className={'page-stack page-stack--tight'}>
                                <span className={'page-section-label'}>{importModalsText?.anonymizeCustomersAndVendors || ''}</span>
                                <span className={'page-help-text'}>{importModalsText?.anonymizeDescription || ''}</span>
                            </div>
                        </label>
                    )}
                    {showGiveCopy && (
                        <label className={'page-checkbox-row'}>
                            <input
                                type={'checkbox'}
                                className={'cr-checkbox page-checkbox-row__input'}
                                name={`${idPrefix}-CopyForUser`}
                                id={`${idPrefix}-copy-for-user`}
                                checked={!!parameters && provideUserCopy}
                                onChange={() => onToggleOnOffSetting && onToggleOnOffSetting(OnOffSettings.userGetsCopy)}
                            />
                            <div className={'page-stack page-stack--tight'}>
                                <span className={'page-section-label'}>{importModalsText?.giveUserACopy || ''}</span>
                                <span className={'page-help-text'}>{importModalsText?.giveUserACopyDescription || ''}</span>
                            </div>
                        </label>
                    )}
                    {showAllowUpload && (
                        <label className={'page-checkbox-row'}>
                            <input
                                type={'checkbox'}
                                className={'cr-checkbox page-checkbox-row__input'}
                                name={`${idPrefix}-AllowAdditionalDocs`}
                                id={`${idPrefix}-allow-additional-docs`}
                                checked={!!parameters && allowUserUpload}
                                onChange={() => onToggleOnOffSetting && onToggleOnOffSetting(OnOffSettings.allowUserUpload)}
                            />
                            <div className={'page-stack page-stack--tight'}>
                                <span className={'page-section-label'}>{importModalsText?.allowAdditionalUpload || ''}</span>
                                <span className={'page-help-text'}>{importModalsText?.allowAdditionalUploadDescription || ''}</span>
                            </div>
                        </label>
                    )}
                </div>
            </>
        );
    };

    const ucPkgImportParameters = (): React.ReactElement => (
        <>
            <div className={'page-stack page-stack--tight'}>
                <span className={'page-section-label'}>
                    {importModalsText?.companyNamePrompt || ''} <span className={'page-required'}>*</span>
                </span>
                <Input
                    type={'text'}
                    error={companyNameError}
                    placeholder={importModalsText?.companyNamePlaceholder || ''}
                    id={'uc-connector-company-name'}
                    name={'uc-connector-company-name'}
                    value={ucParameters.companyName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateMetadata(ImportMetadataKeys.ucMetadataCompanyName, e.currentTarget.value)}
                />
                {companyNameError && (
                    <span className={'cr-input-group__error'}>{importModalsText?.companyNameRequired || ''}</span>
                )}
            </div>
            <div className={'page-stack page-stack--tight'}>
                <span className={'page-section-label'}>{importModalsText?.currencyPrompt || ''}</span>
                <Select
                    id={'uc-connector-currency'}
                    name={'uc-connector-currency'}
                    value={ucParameters.currency}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateMetadata(ImportMetadataKeys.ucMetadataCurrency, e.currentTarget.value)}
                >
                    {currencies.map(c => (
                        <option key={c.code} value={c.code}>{`${c.code} - ${c.name}`}</option>
                    ))}
                </Select>
            </div>
            {!!commonText && (
                <div className={'page-stack page-stack--tight'}>
                    <span className={'page-section-label'}>{importModalsText?.fiscalEndMonthPrompt || ''}</span>
                    <Select
                        id={'uc-connector-fiscal-end-month'}
                        name={'uc-connector-fiscal-end-month'}
                        value={ucParameters.fiscalYearEndMonth}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => updateMetadata(ImportMetadataKeys.ucFiscalEndMonth, e.currentTarget.value)}
                    >
                        {commonText.allMonths.map((m, i) => (
                            <option key={`m-${i}`} value={i.toString()}>{m}</option>
                        ))}
                    </Select>
                </div>
            )}
        </>
    );

    // replaceCalendarWithMonthEndOrToday is only used when this control is invoked from tenantSelection.tsx.
    // Not a concern right now as it's only used for tweaking test parameters.
    const rangeOfTimeOptions = (): React.ReactElement => (
        <div className={'page-stack page-stack--tight'}>
            {(replaceCalendarWithMonthEndOrToday === true) && (
                <>
                    <label className={'page-radio-row'}>
                        <input
                            type={'radio'}
                            className={'cr-radio'}
                            name={`${idPrefix}-collect-through`}
                            id={`${idPrefix}-collect-through-today`}
                            checked={!!parameters && parameters.endOfImport === 'Today'}
                            disabled={!parameters}
                            onChange={(e) => { if (e.currentTarget.checked) { onCollectThroughChanged && onCollectThroughChanged('Today'); } }}
                        />
                        <span className={'page-radio-row__label'}>{importModalsText?.collectThroughCurrentDay || ''}</span>
                    </label>
                    <label className={'page-radio-row'}>
                        <input
                            type={'radio'}
                            className={'cr-radio'}
                            name={`${idPrefix}-collect-through`}
                            id={`${idPrefix}-collect-through-last-full-month`}
                            checked={!!parameters && parameters.endOfImport === 'LastFullMonth'}
                            disabled={!parameters}
                            onChange={(e) => { if (e.currentTarget.checked) { onCollectThroughChanged && onCollectThroughChanged('LastFullMonth'); } }}
                        />
                        <span className={'page-radio-row__label'}>{importModalsText?.collectThroughLastFullMonth || ''}</span>
                    </label>
                </>
            )}
            <label className={'page-radio-row'}>
                <input
                    type={'radio'}
                    className={'cr-radio'}
                    name={`${idPrefix}-fullFiscalYear`}
                    id={`${idPrefix}-id-full-fiscal-year`}
                    checked={currentYearOnly}
                    disabled={!parameters}
                    onChange={() => {
                        setCurrentYearOnly(true);
                        onPeriodsToCollectChanged && onPeriodsToCollectChanged(0);
                    }}
                />
                <span className={'page-radio-row__label'}>{importModalsText?.fiscalYearToDateOnly || ''}</span>
            </label>
            <label className={'page-radio-row'}>
                <input
                    type={'radio'}
                    className={'cr-radio'}
                    name={`${idPrefix}-fullFiscalYear`}
                    id={`${idPrefix}-id-full-fiscal-year-plus`}
                    checked={!currentYearOnly}
                    disabled={!parameters}
                    onChange={() => {
                        setCurrentYearOnly(false);
                        onPeriodsToCollectChanged && onPeriodsToCollectChanged(fullFYearsToCollect);
                    }}
                />
                <span className={'page-radio-row__label'}>{importModalsText?.fiscalYearToDatePlus || ''}</span>
            </label>
            {/* Only shown when the "...plus:" radio is selected; hidden entirely for the fiscal-year-to-date-only choice. */}
            {!currentYearOnly && (
                <div className={'page-field-indent'}>
                    <div className={'page-inline-field'}>
                        <div style={{ width: 80 }}>
                            <Input
                                type={'number'}
                                disabled={!parameters}
                                name={`${idPrefix}-countFullFiscalYears`}
                                id={`${idPrefix}-count-full-fiscal-years`}
                                value={fullFYearsToCollect}
                                min={1}
                                max={maxCollectionPeriods}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    const fullFYears = parseInt(event.currentTarget.value);
                                    if (!isNaN(fullFYears)) {
                                        if ((fullFYears > 0) && (fullFYears <= maxCollectionPeriods)) {
                                            setFullFYearsToCollect(fullFYears);
                                            onPeriodsToCollectChanged && onPeriodsToCollectChanged(fullFYears);
                                        }
                                    }
                                }}
                            />
                        </div>
                        <span className={'page-body-text'}>
                            {(fullFYearsToCollect === 1 ? importModalsText?.fiscalYear : importModalsText?.fiscalYears) || ''}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className={'page-stack'}>
            {/* Collect data through — native date field styled as a design-system input. The container applies
                getUsableDay (end-of-month snapping) when the month/year changes, so the value reflects that. */}
            {!!datePickerParameters && (replaceCalendarWithMonthEndOrToday !== true) && (
                <div className={'page-stack page-stack--tight'}>
                    <span className={'page-section-label'}>{importModalsText?.selectDateThroughPrompt || ''}</span>
                    <div className={'cr-input'} style={{ maxWidth: 376 }}>
                        <input
                            type={'date'}
                            className={'cr-input__control page-cursor-pointer'}
                            value={datePickerInputValue}
                            min={!!noTimeRestrictionForDataCollection ? undefined : toDateInputValue(datePickerParameters.minDate)}
                            max={!!noTimeRestrictionForDataCollection ? undefined : toDateInputValue(datePickerParameters.maxDate)}
                            onBlur={(e) => {
                                const value = e.currentTarget.value;
                                if (!value) {
                                    return;
                                }
                                // Parse the yyyy-MM-dd value directly (avoids the UTC shift new Date(string) would apply).
                                const [year, month, day] = value.split('-').map(Number);
                                onMostRecentMonthChange && onMostRecentMonthChange({ year, month: month - 1, day });
                            }}
                            onChange={(e) => {
                                // Always a valid date
                                const value = e.currentTarget.value;
                                if (!value) {
                                    return;
                                }
                                setDatePickerInputValue(value);
                            }}
                        />
                    </div>
                </div>
            )}

            <div className={'page-divider'} />

            {/* Collection period */}
            <div className={'page-stack page-stack--tight'}>
                <span className={'page-section-label'}>{importModalsText?.collectionPeriod || ''}</span>
                {rangeOfTimeOptions()}
            </div>

            {/* Basis of accounting */}
            {disableBasisOfAccounting !== true && (
                <>
                    <div className={'page-divider'} />
                    <div className={'page-stack page-stack--tight'}>
                        <span className={'page-section-label page-inline-field page-inline-field--tight'}>
                            {importModalsText?.selectBasisOfAccounting || ''}
                            <span
                                tabIndex={0}
                                role={'button'}
                                aria-label={importModalsText?.selectBasisOfAccounting || ''}
                                className={'page-tooltip-trigger'}
                                onMouseEnter={() => setShowBasisTooltip(true)}
                                onMouseLeave={() => setShowBasisTooltip(false)}
                                onFocus={() => setShowBasisTooltip(true)}
                                onBlur={() => setShowBasisTooltip(false)}
                            >
                                <FontAwesomeIcon icon={faCircleInfo} className={'page-tooltip-trigger__icon'} aria-hidden />
                                {showBasisTooltip && (
                                    <span role={'tooltip'} className={'page-tooltip'}>
                                        {importModalsText?.selectBasisOfAccountingTooltip || ''}
                                        <span className={'page-tooltip__arrow'} />
                                    </span>
                                )}
                            </span>
                        </span>
                        {basisOfAccounting()}
                    </div>
                </>
            )}

            {/* Connector-specific parameters */}
            {connectorType === ConnectorType.fullIntegration && accountingPkgImportParameters()}
            {connectorType === ConnectorType.universalConnector && ucPkgImportParameters()}
            {connectorType === ConnectorType.excelUpload && excelImportParameters()}
        </div>
    );
}
